// karismaSkinBoostersData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const karismaSkinBoosters: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Karisma Skin Booster Dubai",
            "description": "",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Karisma Skin Booster"],
        "hero": {
            "title": "Karisma Skin Booster",
            "subtitle": "Revitalize Your Radiance with Karisma Skin Booster",
            "description": "Bid farewell to fine lines, dullness and fatigued skin as the Karisma active formula stimulates collagen production, restoring elasticity and a natural radiance. Ideal for every skin type, it leaves skin looking brighter and feeling more hydrated and plump as well after the first session. Discover the new you with Karisma Skin Booster — your skin at its most radiant."
        },
        "howItWorks": {
            "title": "What is Karisma Skin Booster?",
            "description": "Karisma Skin Booster is a revolutionary bio-revitalisation treatment that hydrates and regenerates the skin from within. It features advanced ingredients, including collagen biostimulators, that stimulate skin renewal, enhance collagen production and replenish elasticity. The no-down-time treatment helps to minimize fine lines, texturize skin and leave a subtle glow of skin for days. This delicate treatment is perfect for all skin types while providing long-lasting hydration and visible improvements in both skin tone and firmness, keeping the skin looking plump and fresh after each session."
        },
        "procedureSteps": {
            "heading": "Karisma Skin Booster Procedure Steps",
            "para": "Karisma Skin Booster treatment uses an advanced formula of natural collagen, hyaluronic and other bioactive substances infused directly into the skin. These ingredients are injected into the dermis, where they help restore lost moisture and promote collagen production and improve the skin's texture and elasticity.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Involve a detailed consultation with a skincare expert to evaluate your skin type, issues, and targets.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Cleansing",
                    "desc": "This cleanses the skin to remove impurities or makeup.",
                    "icon": "fa-solid fa-soap"
                },
                {
                    "title": "Topical Anesthesia (if needed)",
                    "desc": "A numbing cream can be used to lessen any discomfort from the injections.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Injection",
                    "desc": "Karisma Skin Booster is injected into the targeted area using micro-needles, mostly in areas that require hydration or volume correction.",
                    "icon": "fa-solid fa-needle"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "There are many benefits of Karisma Skin Booster that can go beyond improving the surface level of the skin. This offering is more in-depth and provides a longer-lasting fix to a number of skin issues.",
            "items": [
                {
                    "title": "Hydration",
                    "desc": "It adds moisture deep in skin for a plumper, more supple look.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Anti-Aging",
                    "desc": "Stimulates collagen production which smooths out fine lines, wrinkles and sagging skin.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Improved Skin Texture",
                    "desc": "The treatment evens out uneven skin texture, leaving behind a polished and luminous complexion.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Long-Lasting Effects",
                    "desc": "Unlike standard moisturizers or topical treatments, the Karisma Skin Booster offers months' worth of hydration and anti-aging benefits.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Minimally Invasive",
                    "desc": "It's a super fast procedure, virtually no downtime, and there's no surgery involved.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "Versatility",
                    "desc": "It can be applied to the face, neck, décolletage and even hands to revitalize the skin.",
                    "icon": "fa-solid fa-arrows-to-eye"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Karisma Skin Booster is ideal for those wanting to boost their skin's natural radiance, texture and help prevent against signs of aging. But some aspects may decide if this treatment suits you.",
            "items": [
                {
                    "title": "Ideal Candidates",
                    "desc": "People who have dehydrated, dry, lackluster skin.",
                    "icon": "fa-solid fa-circle-check"
                },
                {
                    "title": "Early Signs of Aging",
                    "desc": "Those showing early signs of aging, like fine lines or loss of elasticity.",
                    "icon": "fa-solid fa-face-grin-wink"
                },
                {
                    "title": "Skin Texture Improvement",
                    "desc": "Individuals looking to revamp their skin texture, including those with roughness or mild resurfacing scars.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Non-Invasive Preference",
                    "desc": "People who want a procedure that is non-invasive and requires little recovery time.",
                    "icon": "fa-solid fa-heart-circle-check"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "However, proper preparation is necessary to ensure optimal results for your treatment of Karisma Skin booster. This involves implementing measures pre- and postoperation to ensure optimum safety and efficacy.",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "Be sure to discuss medical history, allergies and concerns with your skin with your specialist.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Avoid Blood Thinners",
                    "desc": "Do not take aspirin, ibuprofen and other blood-thinning medications a few days prior to the treatment, to reduce the risk of bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Prepare Your Skin For Treatment With Lots Of Water",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Avoid Alcohol",
                    "desc": "Do not drink alcohol as least 24-hours prior to the procedure, as this may increase the risk of bruising.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Post-Treatment Care",
                    "desc": "Follow any special aftercare instructions from your clinician, such as avoiding direct sun exposure and using their suggested skincare products.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Karisma Skin Booster provides long-lasting, visible results that makes your skin look younger, radiant and very hydrated. It is also a great choice for those wishing to reclaim their skin's natural beauty and vitality.",
            "items": [
                {
                    "title": "Immediate Hydration",
                    "desc": "Within days, your skin will feel more hydrated and look plumper.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Long-Term Improvement",
                    "desc": "Over the next few weeks, as collagen production ramps up, you should notice that your skin looks smoother, firmer.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Natural Glow",
                    "desc": "The treatment refreshes the skin to produce a new healthy dew with staying power.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "You can resume your normal activities almost instantly, with only mild redness or swelling that fades quickly.",
                    "icon": "fa-solid fa-stopwatch"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Before & After Results",
            "subheading": "",
            "para": "In this article: Karisma Skin Booster (Hydration + Volume) For the absolute best results, Karisma Skin booster treatment should be performed by trained professionals. It is performed by our trained experts who uses the latest techniques for better results; hence making your skin refreshed and glowing. Elite Body Home provides a safe and effective treatment that is customized to your individual needs with a focus on personalized care and cutting-edge technology.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is the treatment painful?",
                    "answer": "Some patients experience mild discomfort during the injections, but a numbing cream can be applied to minimize pain."
                },
                {
                    "question": "How long do the results last?",
                    "answer": "Results can last up to 6 months, depending on individual skin conditions and aftercare."
                },
                {
                    "question": "When will I see results?",
                    "answer": "You will start to notice an improvement in your skin's hydration and texture within a few days, with full results visible after 2-3 weeks."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Side effects are generally mild and may include redness, swelling, or bruising at the injection sites, which subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "introductionImage": "",
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Basdsfnner-Image-scaled.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-3.jpg"
            ]
        },
        "product": {
            "id": 9,
            "title": "Karisma Skin Booster",
            "price": 199,
            "description": "Karisma Skin Booster is a revolutionary bio-revitalisation treatment that hydrates and regenerates the skin from within. It features advanced ingredients, including collagen biostimulators, that stimulate skin renewal, enhance collagen production and replenish elasticity. The no-down-time treatment helps to minimize fine lines, texturize skin and leave a subtle glow of skin for days. This delicate treatment is perfect for all skin types while providing long-lasting hydration and visible improvements in both skin tone and firmness, keeping the skin looking plump and fresh after each session."
        }
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "إبرة النضارة كاريزما في دبي",
            "description": "",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "إبرة النضارة كاريزما"],
        "hero": {
            "title": "إبرة النضارة كاريزما",
            "subtitle": "أعيدي تألق بشرتك مع إبر كاريزما",
            "description": "ودّعي الخطوط الرفيعة والبشرة الباهتة والمُرهقة، مع تركيبة كاريزما الفعالة التي تُحفز إنتاج الكولاجين وتُعيد المرونة والإشراقة الطبيعية لبشرتك. يناسب العلاج لجميع أنواع البشرة، حيث يمنح البشرة ملمسًا أكثر ترطيبًا ومظهرًا أكثر امتلاءً بدءًا من الجلسة الأولى. اكتشفي النسخة الأجمل من نفسك مع علاج كاريزما، للحصول على بشرة في أقصى درجات تألقها."
        },
        "howItWorks": {
            "title": "ما هي إبرة النضارة كاريزما؟",
            "description": "تُعد إبر كاريزما علاج مبتكر لإعادة تجديد البشرة، حيث يعمل على ترطيب وتجديد البشرة من الداخل. يعتمد العلاج على مكونات متقدمة، بما في ذلك محفزات إنتاج الكولاجين الحيوية، التي تحفز تجديد الخلايا، وتعزز إنتاج الكولاجين، وتستعيد مرونة البشرة. يتميز هذا العلاج بأنه لا يحتاج إلى فترة تعافي، ويساعد في تقليل الخطوط الدقيقة وتحسين ملمس البشرة، مع ترك لمسة من التألق الطبيعي للبشرة الذي يستمر لعدة أيام. يعد هذا العلاج مثالي لجميع أنواع البشرة، حيث يوفر ترطيباً يدوم طويلاً ونتائج مرئية في تحسين لون البشرة ومرونتها، مما يجعل البشرة تبدو ممتلئة ومنتعشة بعد كل جلسة."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء علاج كاريزما",
            "para": "يستخدم علاج كاريزما تركيبة متقدمة من الكولاجين الطبيعي، وحمض الهيالورونيك، وغيرها من المواد الحيوية التي تُحقن مباشرة في البشرة. تساعد هذه المكونات في استعادة الرطوبة المفقودة، وتعزيز إنتاج الكولاجين، وتحسين ملمس البشرة ومرونتها.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "تشمل استشارة تفصيلية مع خبير العناية بالبشرة لتقييم نوع بشرتك والمشكلات التي تعاني منها، بالإضافة إلى تحديد الأهداف التي ترغب في تحقيقها.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "التنظيف",
                    "desc": "تُنظف البشرة لإزالة الشوائب أو مستحضرات التجميل المتبقية.",
                    "icon": "fa-solid fa-soap"
                },
                {
                    "title": "التخدير الموضعي (إذا لزم الأمر)",
                    "desc": "يمكن استخدام كريم مخدر لتقليل أي شعور بالألم أثناء الحقن.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "الحقن",
                    "desc": "تُحقن إبر كاريزما في المنطقة المستهدفة باستخدام إبر دقيقة، وعادةً ما يتم الحقن في المناطق التي تحتاج إلى ترطيب أو تصحيح الحجم.",
                    "icon": "fa-solid fa-needle"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "توفر إبر كاريزما العديد من الفوائد التي تتجاوز تحسين سطح البشرة فقط. فيُعد هذا العلاج أكثر تعمقًا ويوفر حلاً طويل الأمد لعدد من مشكلات البشرة.",
            "items": [
                {
                    "title": "الترطيب المستمر",
                    "desc": "يضيف رطوبة عميقة للبشرة، مما يمنحها مظهراً ممتلئاً وأكثر نضارة.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "مكافحة الشيخوخة",
                    "desc": "يحفز إنتاج الكولاجين، مما يساهم في تنعيم الخطوط الدقيقة والتجاعيد وشد الجلد المترهل.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يعمل العلاج على تحسين نسيج البشرة غير المتساوي، ويمنح البشرة مظهرًا ناعمًا ومشرقًا.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "تقدم إبر كاريزما فوائد طويلة الأمد من حيث الترطيب ومكافحة علامات الشيخوخة على عكس المرطبات التقليدية أو العلاج الموضعي.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "إجراء غير جراحي",
                    "desc": "يُعد إجراء سريع للغاية، لا يتطلب وقتاً طويلاً للتعافي، ولا يتضمن أي جراحة.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "مرونة الإجراء",
                    "desc": "يمكن استخدامه للوجه والرقبة ومنطقة أعلى الصدر وحتى اليدين لتجديد البشرة.",
                    "icon": "fa-solid fa-arrows-to-eye"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يُعد علاج كاريزما خياراً مثالياً لمن يسعى إلى تعزيز إشراقة بشرته الطبيعية، وتحسين ملمسها، والمساعدة في الوقاية من علامات التقدم في العمر. ومع ذلك، هناك بعض العوامل التي تحدد ما إذا كان هذا العلاج مناسباً لك.",
            "items": [
                {
                    "title": "المرشحون المثاليون",
                    "desc": "الأشخاص الذين يعانون من جفاف البشرة أو نقص في الترطيب أو بهتان اللون.",
                    "icon": "fa-solid fa-circle-check"
                },
                {
                    "title": "علامات الشيخوخة المبكرة",
                    "desc": "الأشخاص الذين يعانون من علامات الشيخوخة المبكرة مثل الخطوط الدقيقة أو فقدان المرونة.",
                    "icon": "fa-solid fa-face-grin-wink"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "الأفراد الذين يرغبون في تحسين ملمس بشرتهم، خاصةً من يعانون من الخشونة أو الندبات البسيطة.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "تفضيل العلاجات غير الجراحية",
                    "desc": "الأشخاص الذين يبحثون عن علاج غير جراحي وبفترة تعافي قصيرة جدًا.",
                    "icon": "fa-solid fa-heart-circle-check"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يجب الالتزام ببعض الخطوات التحضيرية قبل العلاج وبعده لضمان السلامة والفعالية والحصول على أفضل النتائج من جلسة إبر كاريزما.",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "كن متأكد من مناقشة تاريخك الطبي، وأي مخاوف تتعلق بالبشرة مع الطبيب المتخصص، وإبلاغه إذا كنت تعاني من أي حساسية.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "تجنب مُميعات الدم",
                    "desc": "امتنع عن تناول الأسبرين، أو الإيبوبروفين، أو غيرها من الأدوية المُميعة للدم قبل عدة أيام من موعد الجلسة لتقليل احتمالية الكدمات.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "حافظ على ترطيب جسمك",
                    "desc": "اشرب كميات كافية من الماء لتحضير البشرة للعلاج.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "يجب الامتناع عن الكحول",
                    "desc": "امتنع عن شرب الكحول لمدة 24 ساعة على الأقل قبل الجلسة لأنه قد يرفع من احتمالية حدوث الكدمات.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "العناية بعد العلاج",
                    "desc": "التزم بإرشادات الطبيب للعناية بالبشرة بعد العلاج مثل تجنب التعرض المباشر للشمس، واستخدام المنتجات الموصى بها.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "توفر إبر كاريزما نتائج مرئية وطويلة الأمد، تمنح بشرتك مظهرًا أكثر شبابًا وإشراقًا وترطيبًا عميقًا، حيث يُعد خيارًا مثاليًا لمن يرغبون في استعادة جمال وحيوية بشرتهم الطبيعية.",
            "items": [
                {
                    "title": "ترطيب فوري للبشرة",
                    "desc": "ستلاحظ ترطيب عميق للبشرة، وستبدو ممتلئة وأكثر نضارة في غضون أيام قليلة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تحسن طويل الأمد",
                    "desc": "ستلاحظ تحسنًا في نعومة البشرة وتماسكها على مدار الأسابيع التالية مع تحفيز إنتاج الكولاجين.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "إشراقة طبيعية للبشرة",
                    "desc": "يساهم العلاج في إنعاش البشرة ومنحها إشراقة صحية تدوم لفترة طويلة.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "لا يتطلب فترة تعافي",
                    "desc": "يمكنك استئناف أنشطتك اليومية مباشرةً بعد الجلسة، مع احتمالية حدوث احمرار بسيط أو تورم يزول سريعًا.",
                    "icon": "fa-solid fa-stopwatch"
                }
            ]
        },
        "aftercareTips": {
            "heading": "قبل وبعد العلاج",
            "subheading": "",
            "para": "في هذا المقال: إبر النضارة كاريزما (ترطيب البشرة وتصحيح الحجم) يجب أن يُجرى علاج كاريزما على يد متخصصين مدربين للحصول على أفضل النتائج الممكنة. يُنفذ العلاج في ايليت بودي هوم من قبل خبرائنا باستخدام أحدث التقنيات لتحقيق أفضل نتائج، مما يمنح بشرتك مظهرًا متجددًا ومشرقًا. نُقدم في ايليت بودي هوم علاجًا آمنًا وفعّالًا، مصممًا بعناية ليتناسب مع احتياجاتك الفردية، مع التركيز على العناية الشخصية والتقنيات الحديثة.",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل العلاج مؤلم؟",
                    "answer": "بعض المرضى يشعرون بانزعاج بسيط أثناء الحقن، ولكن يمكن استخدام كريم مخدر لتقليل الألم."
                },
                {
                    "question": "كم تدوم النتائج؟",
                    "answer": "يمكن أن تدوم النتائج حتى 6 أشهر، حسب حالة البشرة الفردية والعناية بعد العلاج."
                },
                {
                    "question": "متى سأرى النتائج؟",
                    "answer": "ستلاحظ تحسنًا في ترطيب البشرة وملمسها خلال أيام قليلة، مع ظهور النتائج الكاملة بعد 2-3 أسابيع."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية عادة ما تكون خفيفة وقد تشمل احمرارًا أو تورمًا أو كدمات في مواقع الحقن، تختفي خلال بضعة أيام."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []

    }
};
